/**
 * 지오코딩 클라이언트. 모든 호출은 Cloud Function `geocodeAddress` 경유.
 * 서버에서 Kakao Local API (primary) + V-World (fallback) 순서로 시도.
 */
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";

export interface VworldPoint {
  x: number; // lng
  y: number; // lat
  matched: string;
  type: "ROAD" | "PARCEL";
  provider?: "kakao" | "vworld";
  gu?: string;
  dong?: string;
  roadMismatch?: boolean; // 입력과 다른 "N번길"로 매칭됨 — 유사주소 스냅 의심(함정 #8)
}

interface CallableResult {
  lat: number;
  lng: number;
  matched: string;
  type: "ROAD" | "PARCEL";
  provider: "kakao" | "vworld";
  gu?: string;
  dong?: string;
  roadMismatch?: boolean;
}

const _callable = httpsCallable<{ address: string }, CallableResult | null>(
  functions,
  "geocodeAddress",
);

/**
 * 주소 → 좌표.
 * 실패/미매칭은 null 반환. 인증/설정 오류 등 복구 불가능한 에러는 throw.
 */
export async function geocodeAddress(address: string): Promise<VworldPoint | null> {
  if (!address) return null;
  const res = await _callable({ address });
  const data = res.data;
  if (!data) return null;
  return {
    x: data.lng,
    y: data.lat,
    matched: data.matched,
    type: data.type,
    provider: data.provider,
    gu: data.gu,
    dong: data.dong,
    roadMismatch: data.roadMismatch,
  };
}

/** 하버사인 거리 (km) */
export function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
